/**
 * Device identity used for subscription (activation-token) binding.
 *
 * HISTORY / WHY THIS IS A UUID: this used to "fingerprint" the machine by
 * hex-encoding `userAgent|language|hardwareConcurrency|colorDepth`. The user
 * agent embeds the packaged app + Chromium version, so the recomputed value
 * changed on every app update, and identical hardware produced identical ids.
 * Any fresh profile/localStorage then minted an id that no longer matched the
 * one bound server-side → `DEVICE_MISMATCH` → booth licence friction fleet-wide.
 *
 * Rules now:
 * - An EXISTING persisted deviceId (any format, including the legacy hex
 *   fingerprint) is frozen forever: settingsStore only calls this as the
 *   initializer for an empty store, and nothing may ever recompute or
 *   "migrate" a stored value. Server bindings stay intact across updates.
 * - Fresh installs mint a random, version-independent id exactly once.
 *   The `dvc-` prefix distinguishes new-style ids from legacy fingerprints.
 */
export function generateDeviceId(): string {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : // Very old shells without randomUUID: timestamp + two random segments.
        `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random()
          .toString(36)
          .slice(2, 10)}`
  return `dvc-${uuid}`
}
